import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import PayeeCombobox from '../components/PayeeCombobox';
import { getAllocations, getPayees, createPaymentRequest, uploadDocument } from '../services/api';
import { useToken } from '../hooks/useToken';
import type { Payee, Allocation } from '../types';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

export default function NewRequest() {
  const getToken = useToken();
  const navigate = useNavigate();

  const [payees, setPayees] = useState<Payee[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);

  const [amount, setAmount] = useState('');
  const [payeeId, setPayeeId] = useState('');
  const [allocationId, setAllocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getToken()
      .then(async (t) => {
        const [p, a] = await Promise.all([getPayees(t), getAllocations(t, { excludeClosed: true })]);
        setPayees(p.data.filter((x) => x.is_active));
        setAllocations(a.data);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingRef(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsedAmount = parseFloat(amount.replace(/[^0-9.]/g, ''));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (!payeeId) { setError('Please select a Payee.'); return; }
    if (!allocationId) { setError('Please select a Fund Pool / Project.'); return; }

    const selectedPayee = payees.find((p) => p.payee_id === Number(payeeId));
    if (!selectedPayee?.preferred_method_id) {
      setError('This payee does not have a payment method set. An Onyx coordinator must update the payee record before a request can be submitted.');
      return;
    }
    const methodId = selectedPayee.preferred_method_id;

    setSubmitting(true);
    try {
      const token = await getToken();
      const { payment_request_id } = await createPaymentRequest(token, {
        allocation_id: Number(allocationId),
        payee_id: Number(payeeId),
        payment_method_id: methodId,
        amount: parsedAmount,
        bhsd_approver_notes: notes.trim() || null,
      });

      // Upload documents — surface any errors to the user
      const uploadErrors: string[] = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('payment_request_id', String(payment_request_id));
        fd.append('document_type', 'Other');
        await uploadDocument(token, fd).catch((e: unknown) => {
          uploadErrors.push(`${file.name}: ${e instanceof Error ? e.message : String(e)}`);
        });
      }

      navigate(`/requests/${payment_request_id}`, {
        state: uploadErrors.length > 0
          ? { uploadWarning: `${uploadErrors.length} document(s) failed to upload — you can retry from this page:\n${uploadErrors.join('\n')}` }
          : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  const selectedAlloc = allocations.find((a) => a.allocation_id === Number(allocationId));

  return (
    <Layout pageTitle="New Payment Request">
      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-wrap">
              {error}
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Payee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee <span className="text-red-500">*</span>
            </label>
            <PayeeCombobox
              payees={payees}
              value={payeeId}
              onChange={setPayeeId}
              disabled={loadingRef}
            />
          </div>

          {/* Fund Pool / Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fund Pool / Project <span className="text-red-500">*</span>
            </label>
            <select
              value={allocationId}
              onChange={(e) => setAllocationId(e.target.value)}
              required
              disabled={loadingRef}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">— Select Fund Pool / Project —</option>
              {allocations.map((a) => (
                <option key={a.allocation_id} value={a.allocation_id}>
                  {a.fiscal_year_label} — {a.fund_pool_description ?? a.fund_pool_code} / {a.project_name}
                  {' '}— {formatCurrency(a.remaining_balance)} remaining
                </option>
              ))}
            </select>
            {selectedAlloc && (
              <p className="mt-1 text-xs text-gray-500">
                Remaining balance: {formatCurrency(selectedAlloc.remaining_balance)}
              </p>
            )}
          </div>

          {/* BHSD Approver Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              BHSD Approver Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supporting Documents
            </label>
            <FileUpload onFiles={(f) => setFiles((prev) => [...prev, ...f])} />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-gray-600">
                    <span>{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-red-500 hover:text-red-700 ml-3"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/requests')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel and Discard
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
